import { useState } from 'preact/hooks';
import { ASSETS, getAsset } from '../../content/market';
import { fmtEur, fmtEurPrecise, fmtPct } from '../../engine/format';
import { t } from '../../engine/i18n';
import type { AssetId, AvContract, GameState, Holding } from '../../engine/state';
import { update } from '../../engine/store';
import { DAYS_PER_YEAR } from '../../engine/time';
import {
  AV,
  avAgeYears,
  avValue,
  avWithdrawalPreview,
  canOpenAv,
  depositToAv,
  eurosRate,
  eurosRateFor,
  openAv,
  withdrawFromAv,
} from '../../game/av';
import { priceOf } from '../../game/market';
import {
  PEA,
  canOpenPea,
  depositToPea,
  openPea,
  peaAgeYears,
  peaValue,
  togglePeaMonthly,
  withdrawFromPea,
  withdrawalPreview,
} from '../../game/pea';
import { Card, Line, LineChart } from '../components';
import { formatDuration, signedEur, signedPct } from '../labels';

/**
 * L'onglet Placements : l'état du PEA et de l'assurance-vie, leur évolution, leurs placements et leurs
 * versements. Les achats se font au Marché.
 */
export function PlacementsTab({ state }: { state: GameState }) {
  return (
    <div class="grid">
      {state.pea.open ? (
        <>
          <PeaCard state={state} />
          <HoldingsCard state={state} />
          <FlowsCard state={state} />
        </>
      ) : (
        <OpenPeaCard state={state} />
      )}
      {state.av.open ? (
        <>
          <AvCard state={state} />
          <AvHoldingsCard state={state} />
          <AvFlowsCard state={state} />
        </>
      ) : (
        <OpenAvCard state={state} />
      )}
    </div>
  );
}

function OpenPeaCard({ state }: { state: GameState }) {
  // L'assurance-vie du conseiller peut arriver avant le fonds d'urgence complet : le PEA attend l'étape 2.
  if (state.stage !== 'stage2') {
    return (
      <Card title={t('invest.title')}>
        <p class="muted">{t('invest.peaLater')}</p>
      </Card>
    );
  }
  return (
    <Card title={t('invest.title')}>
      <p>{t('invest.intro')}</p>
      <p class="muted">{t('invest.peaPitch')}</p>
      <button class="button-primary" disabled={!canOpenPea(state)} onClick={() => update(openPea)}>
        {t('invest.open')}
      </button>
      <p class="hint">{t('invest.openHint')}</p>
    </Card>
  );
}

/** Ce que vaut le PEA, ce qui y a été versé, et la courbe des deux depuis l'ouverture. */
function PeaCard({ state }: { state: GameState }) {
  const value = peaValue(state);
  const gain = value - state.pea.deposits;
  const age = peaAgeYears(state);
  return (
    <Card title={t('invest.pea')}>
      <Line label={t('invest.value')} value={fmtEur(value)} tone="strong" />
      <Line label={t('invest.deposits')} value={fmtEur(state.pea.deposits)} />
      <Line label={t('invest.gain')} value={signedEur(gain)} tone={gain < 0 ? 'bad' : 'good'} />
      <Line label={t('invest.cash')} value={fmtEur(state.pea.cash)} />
      <ValueChart
        history={state.pea.history}
        value={value}
        deposits={state.pea.deposits}
        age={age}
        label={t('invest.peaChart')}
        legend={t('invest.legendValue')}
        soon={t('invest.chartSoon')}
      />
      <p class="hint">
        {age < PEA.years
          ? t('invest.age', {
              duration: formatDuration(age * DAYS_PER_YEAR),
              left: formatDuration((PEA.years - age) * DAYS_PER_YEAR),
            })
          : t('invest.ageDone', { duration: formatDuration(age * DAYS_PER_YEAR) })}
      </p>
    </Card>
  );
}

/** La valeur d'un placement semaine après semaine, face à ce qui a été versé (en pointillés). */
function ValueChart(props: {
  history: { value: number; deposits: number }[];
  value: number;
  deposits: number;
  age: number;
  label: string;
  legend: string;
  soon: string;
}) {
  const { history, value, deposits, age } = props;
  if (history.length === 0) return <p class="hint">{props.soon}</p>;
  const values = [...history.map((point) => point.value), value];
  const reference = [...history.map((point) => point.deposits), deposits];
  return (
    <>
      <LineChart values={values} reference={reference} label={props.label} />
      <div class="chart-legend">
        <span class={`legend-value ${value < deposits ? 'down' : ''}`}>{props.legend}</span>
        <span>{t('invest.legendDeposits')}</span>
        <span>{t('invest.chartSince', { duration: formatDuration(age * DAYS_PER_YEAR) })}</span>
      </div>
    </>
  );
}

/** Les placements détenus, dans l'ordre du marché. */
function held(holdings: Partial<Record<AssetId, Holding>>): { id: AssetId; holding: Holding }[] {
  return ASSETS.flatMap((asset) => {
    const holding = holdings[asset.id];
    return holding && holding.shares > 0 ? [{ id: asset.id, holding }] : [];
  });
}

/** Chaque placement détenu : sa valeur du jour, et ce qu'il a gagné ou perdu depuis l'achat. */
function HoldingLines({ state, items }: { state: GameState; items: { id: AssetId; holding: Holding }[] }) {
  return (
    <>
      {items.map(({ id, holding }) => {
        const value = holding.shares * priceOf(state, id);
        const gain = value - holding.cost;
        return (
          <div key={id}>
            <Line label={t(getAsset(id).name)} value={fmtEur(value)} />
            <p class={`hint ${gain < 0 ? 'tone-bad' : 'tone-good'}`}>
              {t(holding.shares === 1 ? 'invest.holdingLineOne' : 'invest.holdingLine', {
                shares: holding.shares,
                gain: signedEur(gain),
                pct: signedPct(holding.cost > 0 ? gain / holding.cost : 0),
              })}
            </p>
          </div>
        );
      })}
    </>
  );
}

function HoldingsCard({ state }: { state: GameState }) {
  const items = held(state.pea.holdings);
  return (
    <Card title={t('invest.holdings')}>
      {items.length === 0 && <p class="muted">{t('invest.holdingsNone')}</p>}
      <HoldingLines state={state} items={items} />
      <p class="hint">{t('invest.holdingsHint')}</p>
    </Card>
  );
}

/** Le versement programmé, et l'argent qui entre dans le PEA ou qui en sort. */
function FlowsCard({ state }: { state: GameState }) {
  const [withdrawing, setWithdrawing] = useState(false);
  const value = peaValue(state);
  const preview = withdrawalPreview(state, state.pea.cash);
  return (
    <Card title={t('invest.flows')}>
      <p class={state.pea.monthlyPaused && state.pea.monthly > 0 ? 'warning' : 'muted'}>
        {state.pea.monthly <= 0
          ? t('invest.monthlyNone')
          : state.pea.monthlyPaused
            ? t('invest.monthlyPaused', { amount: fmtEur(state.pea.monthly) })
            : t('invest.monthly', { amount: fmtEur(state.pea.monthly) })}
      </p>
      {state.pea.monthly > 0 && (
        <button onClick={() => update(togglePeaMonthly)}>
          {t(state.pea.monthlyPaused ? 'invest.resume' : 'invest.pause')}
        </button>
      )}
      <label class="field">
        <span class="muted">{t('invest.monthlyAsset')}</span>
        <select
          value={state.pea.monthlyAsset}
          onChange={(event) => {
            const id = (event.target as HTMLSelectElement).value as AssetId;
            update((s) => (s.pea.monthlyAsset = id));
          }}
        >
          {ASSETS.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {t(asset.name)}
            </option>
          ))}
        </select>
      </label>
      <div class="button-row">
        <button disabled={state.cash < 100} onClick={() => update((s) => depositToPea(s, 100))}>
          {t('invest.deposit', { amount: fmtEur(100) })}
        </button>
        <button disabled={state.cash < 1000} onClick={() => update((s) => depositToPea(s, 1000))}>
          {t('invest.deposit', { amount: fmtEur(1000) })}
        </button>
        <button disabled={value <= 0} onClick={() => setWithdrawing(true)}>
          {t('invest.withdraw')}
        </button>
      </div>
      {withdrawing && (
        <div class="offer">
          <p class="warning">
            {preview.closes
              ? t('invest.withdrawClose', { tax: fmtEur(preview.tax), net: fmtEur(preview.amount - preview.tax) })
              : t('invest.withdrawAfter', { amount: fmtEur(preview.amount), tax: fmtEur(preview.tax) })}
          </p>
          <div class="button-row">
            <button
              class="button-primary"
              disabled={!preview.closes && preview.amount <= 0}
              onClick={() => {
                update((s) => withdrawFromPea(s, s.pea.cash));
                setWithdrawing(false);
              }}
            >
              {preview.closes ? t('invest.confirmClose') : t('invest.confirmWithdraw', { amount: fmtEur(preview.amount) })}
            </button>
            <button onClick={() => setWithdrawing(false)}>{t('invest.cancel')}</button>
          </div>
        </div>
      )}
    </Card>
  );
}

const CONTRACT_NAME: Record<AvContract, 'av.online' | 'av.bank'> = { online: 'av.online', bank: 'av.bank' };

/** Les deux contrats côte à côte : leurs frais, et le taux de leur fonds euros cette année. */
function OpenAvCard({ state }: { state: GameState }) {
  const option = (contract: AvContract) => {
    const terms = AV.contracts[contract];
    return (
      <div class="offer" key={contract}>
        <Line label={t(CONTRACT_NAME[contract])} value={fmtPct(eurosRateFor(state, contract))} />
        <p class="hint">
          {t(terms.entryFee > 0 ? 'av.termsFees' : 'av.termsNoFees', {
            entry: fmtPct(terms.entryFee),
            fee: fmtPct(terms.fee),
          })}
        </p>
        <button disabled={!canOpenAv(state)} onClick={() => update((s) => openAv(s, contract))}>
          {t(contract === 'online' ? 'av.openOnline' : 'av.openBank')}
        </button>
      </div>
    );
  };
  return (
    <Card title={t('av.title')}>
      <p>{t('av.intro')}</p>
      <p class="muted">{t('av.taxPitch')}</p>
      <p class="eyebrow">{t('av.compare')}</p>
      {option('online')}
      {option('bank')}
      <p class="hint">{t('av.oneContract')}</p>
    </Card>
  );
}

/** Ce que vaut le contrat, ce qui y a été versé, sa courbe, et le compte à rebours des huit ans. */
function AvCard({ state }: { state: GameState }) {
  const value = avValue(state);
  const gain = value - state.av.deposits;
  const age = avAgeYears(state);
  return (
    <Card title={t('av.mine')}>
      <p class="eyebrow">{t(CONTRACT_NAME[state.av.contract])}</p>
      <Line label={t('invest.value')} value={fmtEur(value)} tone="strong" />
      <Line label={t('invest.deposits')} value={fmtEur(state.av.deposits)} />
      <Line label={t('invest.gain')} value={signedEur(gain)} tone={gain < 0 ? 'bad' : 'good'} />
      <ValueChart
        history={state.av.history}
        value={value}
        deposits={state.av.deposits}
        age={age}
        label={t('av.chart')}
        legend={t('av.legendValue')}
        soon={t('av.chartSoon')}
      />
      <p class="hint">
        {age < AV.years
          ? t('av.age', {
              duration: formatDuration(age * DAYS_PER_YEAR),
              left: formatDuration((AV.years - age) * DAYS_PER_YEAR),
            })
          : t('av.ageDone', { duration: formatDuration(age * DAYS_PER_YEAR), allowance: fmtEur(AV.allowance) })}
      </p>
    </Card>
  );
}

/** Les deux poches du contrat : le fonds euros, garanti, et les unités de compte. */
function AvHoldingsCard({ state }: { state: GameState }) {
  const items = held(state.av.holdings);
  return (
    <Card title={t('av.inside')}>
      <Line label={t('av.euros')} value={fmtEur(state.av.euros)} tone="strong" />
      <p class="hint">
        {t('av.eurosRate', { rate: fmtPct(eurosRate(state)), accrued: fmtEurPrecise(state.av.eurosAccrued) })}
      </p>
      <p class="eyebrow">{t('av.uc')}</p>
      {items.length === 0 && <p class="muted">{t('av.ucNone')}</p>}
      <HoldingLines state={state} items={items} />
      <p class="hint">{t('av.ucHint', { fee: fmtPct(AV.contracts[state.av.contract].fee) })}</p>
    </Card>
  );
}

/** Les versements (frais d'entrée compris) et les retraits, avec l'impôt annoncé avant de confirmer. */
function AvFlowsCard({ state }: { state: GameState }) {
  // Le montant du retrait qu'on envisage ; Infinity pour tout le fonds euros.
  const [amount, setAmount] = useState<number | null>(null);
  const entryFee = AV.contracts[state.av.contract].entryFee;
  const preview = amount === null ? null : avWithdrawalPreview(state, amount);
  return (
    <Card title={t('av.flows')}>
      <p class="muted">
        {entryFee > 0
          ? t('av.entryFee', { rate: fmtPct(entryFee), fee: fmtEur(1000 * entryFee) })
          : t('av.noEntryFee')}
      </p>
      <div class="button-row">
        <button disabled={state.cash < 100} onClick={() => update((s) => depositToAv(s, 100))}>
          {t('invest.deposit', { amount: fmtEur(100) })}
        </button>
        <button disabled={state.cash < 1000} onClick={() => update((s) => depositToAv(s, 1000))}>
          {t('invest.deposit', { amount: fmtEur(1000) })}
        </button>
        <button disabled={state.av.euros < 1000} onClick={() => setAmount(1000)}>
          {t('av.withdraw', { amount: fmtEur(1000) })}
        </button>
        <button disabled={state.av.euros <= 0} onClick={() => setAmount(Infinity)}>
          {t('av.withdrawAll')}
        </button>
      </div>
      {preview && preview.amount > 0 && (
        <div class="offer">
          <p class={preview.tax > 0 ? 'warning' : 'muted'}>
            {preview.tax > 0
              ? t('av.withdrawTax', {
                  amount: fmtEur(preview.amount),
                  // Quelques centimes d'impôt sur quelques jours d'intérêts : on les montre tels quels.
                  tax: fmtEurPrecise(preview.tax),
                  net: fmtEur(preview.amount - preview.tax),
                })
              : t('av.withdrawNoTax', { amount: fmtEur(preview.amount) })}
          </p>
          <div class="button-row">
            <button
              class="button-primary"
              onClick={() => {
                update((s) => withdrawFromAv(s, amount ?? 0));
                setAmount(null);
              }}
            >
              {t('invest.confirmWithdraw', { amount: fmtEur(preview.amount) })}
            </button>
            <button onClick={() => setAmount(null)}>{t('invest.cancel')}</button>
          </div>
        </div>
      )}
      <p class="hint">{t('av.withdrawHint')}</p>
      <p class="hint">{t('av.taxHint', { allowance: fmtEur(AV.allowance) })}</p>
    </Card>
  );
}
